import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  OrgCodePreflightQuery,
  OrgCodePreflightResult,
  OrgDeleteResult,
  OrgOptionQuery,
  OrgOptionRow,
  OrgQuery,
  OrgRow,
  OrgSaveRequest,
  OrgSaveResult
} from '../types/org';

const BASE = '/sys/org';

/**
 * 医疗机构主数据（运营端维护）：医院 / 分院 / 医共体成员 / 社区医院。
 * 简称必须为 2–6 个汉字；层级用 `pathIds` 表达，供数据权限向下穿透。
 */
export function createOrgApi(http: HttpClient) {
  return {
    list(query: OrgQuery = {}): Promise<OrgRow[]> {
      return http.request<OrgRow[]>({ path: BASE, query: toQuery(query) });
    },

    /**
     * 机构快搜下拉（登录即可读，只回 id/机构号/名称/类型）。
     * 表单选机构用它：`list` 需要 `sys:org:manage`，而且返回了太多敏感字段。
     */
    options(query: OrgOptionQuery = {}): Promise<OrgOptionRow[]> {
      return http.request<OrgOptionRow[]>({ path: `${BASE}/options`, query: toQuery(query) });
    },

    /**
     * 机构号预检（不占号）：按「区县码 + 3 位序号」回显将要生成的机构号。
     * 机构号由系统生成，表单只读展示、不提交。
     */
    preflightCode(query: OrgCodePreflightQuery = {}): Promise<OrgCodePreflightResult> {
      return http.request<OrgCodePreflightResult>({ path: `${BASE}/code/preflight`, query: toQuery(query) });
    },

    /** 新增（id 为空）或更新。 */
    save(body: OrgSaveRequest): Promise<OrgSaveResult> {
      return http.request<OrgSaveResult>({ path: BASE, method: 'POST', body });
    },

    /** 逻辑删除：存在下级机构/仓库/科室/账号时后端会拒绝。 */
    remove(id: Id): Promise<OrgDeleteResult> {
      return http.request<OrgDeleteResult>({ path: `${BASE}/${id}`, method: 'DELETE' });
    }
  };
}

export type OrgApi = ReturnType<typeof createOrgApi>;
